import { FieldErrors, UseFormRegister } from 'react-hook-form'
import { TextInput } from 'components/common'
import DateTime from 'components/common/input/dateTime/dateTime'

type BundlePricingFields = {
  price?: number
  onlinePrice?: number
  specialPrice?: number
  specialPriceStartDate?: string | null
  specialPriceEndDate?: string | null
  slashPrice?: number
  slashPriceStartDate?: string | null
  slashPriceEndDate?: string | null
}

type BundlePricingFormShape = {
  pricing?: BundlePricingFields
}

type Props = {
  register: UseFormRegister<any>
  errors: FieldErrors<BundlePricingFormShape>
}

type PricingGroupProps = {
  title: string
  description: string
  children: React.ReactNode
}

const PricingGroup = ({ title, description, children }: PricingGroupProps) => (
  <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
    <p className="text-sm font-semibold text-primary">{title}</p>
    <p className="mt-0.5 text-xs text-gray-500">{description}</p>
    <div className="mt-3.5 grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
      {children}
    </div>
  </div>
)

/**
 * The bundle's own root-level pricing section — independent of the
 * per-child bundleItems pricing. Price is required; the other 7 fields are
 * optional. Shared by the Create Bundle Product form and the Master Data
 * update form (only shown there once the catalogue is already a bundle).
 */
const BundlePricingFields = ({ register, errors }: Props) => {
  const pricingErrors = errors?.pricing

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-400 text-[10px] font-bold text-white">
          !
        </span>
        <p className="text-xs leading-relaxed text-amber-800">
          Changes here go live everywhere at once — updating any field in this
          section automatically updates the price in every store and on every
          aggregator this bundle is sold on.
        </p>
      </div>

      <PricingGroup
        title="Standard pricing"
        description="The bundle's regular in-store and online price."
      >
        <TextInput
          label="Price"
          name="pricing.price"
          type="number"
          step="any"
          required
          register={register}
          error={pricingErrors?.price?.message}
        />
        <TextInput
          label="Online price"
          name="pricing.onlinePrice"
          type="number"
          step="any"
          register={register}
          error={pricingErrors?.onlinePrice?.message}
        />
      </PricingGroup>

      <PricingGroup
        title="Special price"
        description="A temporary discounted price, active only between the dates below."
      >
        <TextInput
          label="Special price"
          name="pricing.specialPrice"
          type="number"
          step="any"
          register={register}
          error={pricingErrors?.specialPrice?.message}
        />
        <DateTime
          label="Starts"
          name="pricing.specialPriceStartDate"
          register={register}
          error={pricingErrors?.specialPriceStartDate?.message}
        />
        <DateTime
          label="Ends"
          name="pricing.specialPriceEndDate"
          register={register}
          error={pricingErrors?.specialPriceEndDate?.message}
        />
      </PricingGroup>

      <PricingGroup
        title="Slash price"
        description="A strikethrough reference price shown alongside the current price, active only between the dates below."
      >
        <TextInput
          label="Slash price"
          name="pricing.slashPrice"
          type="number"
          step="any"
          register={register}
          error={pricingErrors?.slashPrice?.message}
        />
        <DateTime
          label="Starts"
          name="pricing.slashPriceStartDate"
          register={register}
          error={pricingErrors?.slashPriceStartDate?.message}
        />
        <DateTime
          label="Ends"
          name="pricing.slashPriceEndDate"
          register={register}
          error={pricingErrors?.slashPriceEndDate?.message}
        />
      </PricingGroup>
    </div>
  )
}

export default BundlePricingFields
